import {
  navigatorLock,
  NavigatorLockAcquireTimeoutError,
} from "@supabase/auth-js";

/**
 * How long to wait for the Supabase auth storage lock before giving up. auth-js
 * requests this lock with an indefinite wait on init and session reads; a lock
 * held by a frozen or crashed tab is never released, so that wait would
 * otherwise hang forever.
 */
const LOCK_ACQUIRE_TIMEOUT_MS = 10_000;

function isLockAcquireTimeoutError(error: unknown): boolean {
  // Our bounded acquire (below) aborts via signal, which rejects with a
  // DOMException "AbortError"; the fail-fast (0) path throws the auth-js error.
  return (
    error instanceof NavigatorLockAcquireTimeoutError ||
    (error instanceof DOMException && error.name === "AbortError")
  );
}

async function acquireBoundedLockOrProceed<R>(
  name: string,
  fn: () => Promise<R>,
): Promise<R> {
  try {
    return await navigatorLock(name, LOCK_ACQUIRE_TIMEOUT_MS, fn);
  } catch (error) {
    if (!isLockAcquireTimeoutError(error)) {
      throw error;
    }
    console.warn(
      `Supabase auth lock "${name}" was not acquired within ${LOCK_ACQUIRE_TIMEOUT_MS}ms; proceeding without it to avoid hanging. A frozen or crashed tab may be holding it.`,
    );
    return fn();
  }
}

/**
 * A drop-in replacement for auth-js's default `navigatorLock` that never waits
 * indefinitely. auth-js acquires the auth storage lock with `acquireTimeout < 0`
 * (wait forever) during initialization and session reads. When another tab holds
 * that Web Lock and is then frozen, discarded, or crashes, the browser doesn't
 * release it, so this tab's `getSession` never resolves, `onAuthStateChange`
 * never fires, and the app is stranded on the full-screen loader with no error
 * and no telemetry — the multi-tab wedge power users hit.
 *
 * The fail-fast (0) and finite-timeout (> 0) semantics auth-js relies on are
 * preserved, so the auto-refresh tick still skips cleanly when the lock isn't
 * free. Only the indefinite wait is capped: if the lock can't be acquired within
 * {@link LOCK_ACQUIRE_TIMEOUT_MS}, the operation runs without exclusivity rather
 * than hang. The rare cost is a possible concurrent token refresh across tabs,
 * which is strictly better than a permanently stuck app.
 */
export const acquireAuthLockWithoutHanging = <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  // Browsers without the LockManager API get no locking from auth-js either.
  if (!globalThis.navigator?.locks) {
    return fn();
  }

  if (acquireTimeout >= 0) {
    return navigatorLock(name, acquireTimeout, fn);
  }

  return acquireBoundedLockOrProceed(name, fn);
};
